import type { Dispatch } from 'redux';
import { apolloClient } from '../api/apolloClient';

export interface WebsocketEvent {
  type: string;
  postId?: string;
  storyId?: string;
  action?: string;
  likeCount?: number;
  bookmarkCount?: number;
  newScore?: number;
  totalReplyCount?: number;
  // TRIL-508: the acting user's id. When it equals the current viewer, the event is the viewer's
  // own action on another device, so we also flip their hasLiked/hasBookmarked (not just the count).
  actorUserId?: string;
  data?: any;
}

/**
 * Routes known websocket event types to the live-update paths the cache-rendered
 * feed + detail page consume. After TRIL-191 the per-event paths are uniform on
 * the cache side — only one event still dispatches Redux:
 *  - INTERACTION_UPDATED: writes `Post:{id}.viewerInteractions` via cache.modify
 *    only — no Redux dispatch (TRIL-178).
 *  - new_replies: writes `Post:{id}.replyCount` via cache.modify only — no Redux
 *    dispatch (TRIL-191).
 *  - STORY_SCORE_UPDATED: no dispatch — detail-page score reads via Apollo cache
 *    (useStoryScoreUpdates cache.modifies Post:{id}.score, TRIL-178).
 *
 * IMPORTANT for INTERACTION_UPDATED: writes COUNTS for everyone, but flips hasLiked/
 * hasBookmarked ONLY when the actor IS the current viewer (their action on another device) —
 * those booleans are viewer-specific, so User A liking a post must not set hasLiked=true on
 * User B's cache (TRIL-508).
 */
export function dispatchWebsocketEvent(
  dispatch: Dispatch,
  event: WebsocketEvent,
  viewerId?: string | null,
): void {
  switch (event.type) {
    case 'INTERACTION_UPDATED': {
      const { postId, action, likeCount, bookmarkCount, actorUserId } = event;
      if (!postId) return;

      // Mirror the count into the Apollo cache so the detail page + feed cards
      // (Post:{id}.viewerInteractions) stay current without a refetch. hasLiked/
      // hasBookmarked are flipped only when the actor is the current viewer
      // (TRIL-508) — the viewer's own action echoing to their other devices.
      {
        const isActor = !!actorUserId && actorUserId === viewerId;
        const patch: Record<string, number | boolean> = {};
        if (
          (action === 'like' || action === 'unlike') &&
          typeof likeCount === 'number'
        ) {
          patch.likeCount = likeCount;
          if (isActor) patch.hasLiked = action === 'like';
        }
        if (
          (action === 'bookmark' || action === 'unbookmark') &&
          typeof bookmarkCount === 'number'
        ) {
          patch.bookmarkCount = bookmarkCount;
          if (isActor) patch.hasBookmarked = action === 'bookmark';
        }
        if (Object.keys(patch).length > 0) {
          apolloClient.cache.modify({
            id: apolloClient.cache.identify({ __typename: 'Post', id: postId }),
            fields: {
              viewerInteractions(existing) {
                if (!existing) return existing;
                return { ...existing, ...patch };
              },
            },
          });
        }
      }
      break;
    }

    case 'STORY_SCORE_UPDATED': {
      // The detail page reads score via useQuery(GET_POST_BY_ID_QUERY);
      // useStoryScoreUpdates cache.modifies Post:{id}.score (TRIL-178).
      // posts.store is removed (TRIL-203); no Redux dispatch needed here.
      break;
    }

    case 'new_replies': {
      const { postId, totalReplyCount } = event;
      if (!postId || typeof totalReplyCount !== 'number') return;

      // Absolute set of replyCount on the cached Post entity. The detail
      // page subscribes via useQuery(GET_POST_BY_ID_QUERY); the feed cards
      // subscribe via the normalized Post:{id} reference inside getUserFeed.
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({ __typename: 'Post', id: postId }),
        fields: {
          replyCount: () => totalReplyCount,
        },
      });
      break;
    }

    case 'community_reports_updated': {
      // No Redux dispatch — this event only triggers a re-render of the
      // affected post via EventBus. The Post page subscribes via onEvent
      // and triggers a getPostById refetch.
      break;
    }
  }
}
